/**
 * Métriques DÉRIVÉES (combinaisons des stats brutes qu'on collecte). Fonctions
 * PURES → testables hors DOM. Consommées par les fiches joueur / match / pays.
 *
 * Petit échantillon en phase de groupes : les TAUX (conversion, %) ne sont
 * affichés qu'au-delà d'un minimum (les appelants gardent null sinon).
 */
import type { PlayerMatchRow } from "./playerData.ts";
import type { LiveStats } from "./liveSchema.ts";

export interface PlayerMetrics {
  readonly apps: number;
  readonly minutes: number;
  /** Buts / tirs (taux de conversion), null si < 3 tirs. */
  readonly conversion: number | null;
  /** (Buts + passes décisives) ramené à 90 min. */
  readonly gaPer90: number | null;
  /** Passes clés / 90 (créativité). */
  readonly keyPassesPer90: number | null;
  /** Tacles / 90. */
  readonly tacklesPer90: number | null;
  /** % de passes réussies (pondéré par le volume de passes), 0–100. */
  readonly passAcc: number | null;
  /** Duels gagnés / duels totaux (0–1), null si < 5 duels. */
  readonly duelWinPct: number | null;
  /** Dribbles réussis / tentés (0–1), null si < 3 tentatives. */
  readonly dribblePct: number | null;
}

const num = (v: number | null | undefined): number => (typeof v === "number" ? v : 0);

/** Agrège les métriques d'un joueur sur ses matchs du tournoi (cumulé). */
export function computePlayerMetrics(matches: readonly PlayerMatchRow[]): PlayerMetrics | null {
  let apps = 0;
  let minutes = 0;
  let goals = 0;
  let assists = 0;
  let shots = 0;
  let keyPasses = 0;
  let tackles = 0;
  let duelsWon = 0;
  let duelsTotal = 0;
  let dribOk = 0;
  let dribAtt = 0;
  let passAccWeighted = 0; // % de passes pondéré par le volume (Σ %·passes / Σ passes)
  let passTot = 0;
  for (const m of matches) {
    if (num(m.minutes) > 0) apps += 1;
    minutes += num(m.minutes);
    goals += num(m.goals);
    assists += num(m.assists);
    shots += num(m.shots);
    keyPasses += num(m.keyPasses);
    tackles += num(m.tackles);
    duelsWon += num(m.duelsWon);
    duelsTotal += num(m.duelsTotal);
    dribOk += num(m.dribbles);
    dribAtt += num(m.dribblesAtt);
    if (m.passAcc != null && num(m.passes) > 0) {
      passAccWeighted += m.passAcc * num(m.passes);
      passTot += num(m.passes);
    }
  }
  if (apps === 0) return null;
  const per90 = (x: number): number | null => (minutes > 0 ? (x / minutes) * 90 : null);
  return {
    apps,
    minutes,
    conversion: shots >= 3 ? goals / shots : null,
    gaPer90: per90(goals + assists),
    keyPassesPer90: per90(keyPasses),
    tacklesPer90: per90(tackles),
    passAcc: passTot > 0 ? passAccWeighted / passTot : null,
    duelWinPct: duelsTotal >= 5 ? duelsWon / duelsTotal : null,
    dribblePct: dribAtt >= 3 ? dribOk / dribAtt : null,
  };
}

export interface Dominance {
  /** Part de domination domicile / extérieur (0–100, somme = 100). */
  readonly home: number;
  readonly away: number;
}

// Pondération d'un « indice de contrôle » : la qualité des occasions (xG) pèse
// le plus, puis la possession, puis le volume de tirs / corners.
const DOM_WEIGHTS: ReadonlyArray<{ key: string; w: number }> = [
  { key: "xg", w: 0.35 },
  { key: "possession", w: 0.25 },
  { key: "shots", w: 0.2 },
  { key: "shotsOnTarget", w: 0.1 },
  { key: "corners", w: 0.1 },
];

/** Indice de domination par camp (0–100). null si aucune stat exploitable. */
export function computeDominance(stats: LiveStats | null | undefined): Dominance | null {
  if (!stats) return null;
  let homeScore = 0;
  let totalW = 0;
  for (const { key, w } of DOM_WEIGHTS) {
    const pair = stats[key];
    if (!pair) continue;
    const [h, a] = pair;
    if (h == null || a == null) continue;
    const sum = h + a;
    homeScore += w * (sum > 0 ? h / sum : 0.5);
    totalW += w;
  }
  if (totalW === 0) return null;
  const home = Math.round((homeScore / totalW) * 100);
  return { home, away: 100 - home };
}

export interface Deserved {
  readonly label: string;
  /** "over" = a marqué plus que ses xG ; "under" = a gâché ; "fair" = logique. */
  readonly tone: "over" | "under" | "fair";
}

/** « Résultat mérité » : compare les buts marqués aux xG. */
export function deservedResult(xg: number | null | undefined, goals: number | null | undefined): Deserved | null {
  if (xg == null || goals == null) return null;
  const diff = goals - xg;
  if (diff >= 0.8) return { label: "réalisme clinique", tone: "over" };
  if (diff <= -0.8) return { label: "manque de réalisme", tone: "under" };
  return { label: "fidèle au jeu", tone: "fair" };
}

export interface AttackEfficiency {
  /** Matchs avec une donnée xG (les autres sont ignorés de l'agrégat). */
  readonly matches: number;
  readonly goals: number;
  readonly xg: number;
  readonly shots: number;
  /** Buts − xG cumulés (surperformance de finition). */
  readonly gain: number;
  /** Buts / xG (1 = conforme, > 1 = clinique). null si xG cumulé trop faible. */
  readonly ratio: number | null;
  /** Buts / tirs (taux de conversion). null si < 8 tirs. */
  readonly conversion: number | null;
  readonly tone: "over" | "under" | "fair";
  /** Assez d'occasions (xG cumulé ≥ 3) pour INTERPRÉTER l'écart, pas juste le décrire ?
   *  En dessous, l'écart buts−xG est surtout du bruit → ne pas porter de jugement. */
  readonly confident: boolean;
}

/**
 * EFFICACITÉ OFFENSIVE d'une équipe sur le tournoi : agrège buts / xG / tirs de ses
 * matchs (un match sans xG est ignoré). Le ratio buts/xG dit si l'attaque SUR- ou
 * SOUS-performe ses occasions ; la conversion = buts/tirs. Pur → testable.
 */
export function computeAttackEfficiency(
  samples: ReadonlyArray<{ goals: number; xg: number | null | undefined; shots: number | null | undefined }>,
): AttackEfficiency | null {
  let matches = 0;
  let goals = 0;
  let xg = 0;
  let shots = 0;
  for (const s of samples) {
    if (s.xg == null) continue; // pas de xG sur ce match → hors agrégat
    matches += 1;
    goals += num(s.goals);
    xg += s.xg;
    shots += num(s.shots);
  }
  if (matches === 0 || xg < 0.5) return null;
  const ratio = goals / xg;
  const tone: "over" | "under" | "fair" = ratio >= 1.15 ? "over" : ratio <= 0.85 ? "under" : "fair";
  return {
    matches,
    goals,
    xg,
    shots,
    gain: goals - xg,
    ratio,
    conversion: shots >= 8 ? goals / shots : null,
    tone,
    confident: xg >= 3, // assez d'occasions pour interpréter l'écart (sinon : variance)
  };
}
